import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Search, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { startOfISOWeek, addDays, getISOWeek, getMonth, getYear, format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useApp } from '../contexts/AppContext';
import { api } from '../api/client';
import TaskModal from './TaskModal';
import { StatusBadge, toast } from './UI';

const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

function quarterWeeks(year, quarter) {
  const firstMonth = (quarter - 1) * 3;
  const lastMonth = firstMonth + 2;

  let cursor = startOfISOWeek(new Date(year, firstMonth, 1));
  const weeks = [];

  for (let safety = 0; safety < 20; safety++) {
    const thursday = addDays(cursor, 3);
    const tMonth = getMonth(thursday);
    const tYear = getYear(thursday);

    if (tYear > year || (tYear === year && tMonth > lastMonth)) break;
    if (tYear < year || tMonth < firstMonth) {
      cursor = addDays(cursor, 7);
      continue;
    }

    weeks.push({
      weekNum: getISOWeek(thursday),
      monday: format(cursor, 'yyyy-MM-dd'),
      mondayDate: new Date(cursor),
      sundayDate: addDays(cursor, 6),
      monthIndex: tMonth,
    });
    cursor = addDays(cursor, 7);
  }

  const monthGroups = {};
  weeks.forEach(w => {
    if (!monthGroups[w.monthIndex]) monthGroups[w.monthIndex] = [];
    monthGroups[w.monthIndex].push(w);
  });
  return weeks.map((w, i) => {
    const group = monthGroups[w.monthIndex];
    return {
      ...w,
      isFirstOfMonth: group[0].weekNum === w.weekNum,
      weeksInMonth: group.length,
      monthName: MONTH_NAMES[w.monthIndex],
      isFirstWeekOfQuarter: i === 0,
    };
  });
}

function quarterPeriodDate(year, quarter) {
  return format(new Date(year, (quarter - 1) * 3, 1), 'yyyy-MM-dd');
}
function monthPeriodDate(year, monthIndex) {
  return format(new Date(year, monthIndex, 1), 'yyyy-MM-dd');
}

export default function Planung() {
  const { tasks, refreshTasks, isDone } = useApp();
  const today = new Date();
  const [year, setYear] = useState(getYear(today));
  const [quarter, setQuarter] = useState(Math.floor(getMonth(today) / 3) + 1);
  const [editTask, setEditTask] = useState(null);
  const [modalInitial, setModalInitial] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // string key like 'q' / 'm-0' / 'w-2025-01-06' / 'panel'
  const [panelOpen, setPanelOpen] = useState(true);
  const [panelSearch, setPanelSearch] = useState('');
  const [goalsById, setGoalsById] = useState({});

  useEffect(() => {
    let cancelled = false;
    api.getGoals(year).then(rows => {
      if (cancelled) return;
      const m = {};
      if (Array.isArray(rows)) for (const g of rows) m[g.id] = g;
      setGoalsById(m);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [year]);

  const weeks = useMemo(() => quarterWeeks(year, quarter), [year, quarter]);
  const totalWeeks = weeks.length;
  const qPeriod = quarterPeriodDate(year, quarter);

  const tasksByCell = useMemo(() => {
    const out = { quarter: [], months: {}, weeks: {} };
    for (const t of tasks) {
      if (!t.scope || t.scope === 'task' || !t.period_date) continue;
      const pd = t.period_date.split('T')[0];
      if (t.scope === 'quarter' && pd === qPeriod) out.quarter.push(t);
      else if (t.scope === 'month') {
        const m = weeks.find(w => monthPeriodDate(year, w.monthIndex) === pd);
        if (m) {
          if (!out.months[m.monthIndex]) out.months[m.monthIndex] = [];
          out.months[m.monthIndex].push(t);
        }
      } else if (t.scope === 'week') {
        if (!out.weeks[pd]) out.weeks[pd] = [];
        out.weeks[pd].push(t);
      }
    }
    return out;
  }, [tasks, weeks, qPeriod, year]);

  const panelTasks = useMemo(() => {
    let list = tasks.filter(t =>
      (!t.scope || t.scope === 'task') &&
      !isDone(t)
    );
    const q = panelSearch.trim().toLowerCase();
    if (q) list = list.filter(t => t.title.toLowerCase().includes(q) || (t.next_step || '').toLowerCase().includes(q));
    list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [tasks, panelSearch, isDone]);

  const openExisting = (t) => { setEditTask(t); setModalInitial(null); setShowModal(true); };
  const openNew = (initial) => { setEditTask(null); setModalInitial(initial); setShowModal(true); };

  const prevQuarter = () => {
    if (quarter === 1) { setYear(y => y - 1); setQuarter(4); }
    else setQuarter(q => q - 1);
  };
  const nextQuarter = () => {
    if (quarter === 4) { setYear(y => y + 1); setQuarter(1); }
    else setQuarter(q => q + 1);
  };

  const onDragStart = (taskId) => (e) => {
    setDraggedId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', taskId); } catch {}
  };
  const onDragOver = (key) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (key !== dropTarget) setDropTarget(key);
  };
  const onDragLeave = () => setDropTarget(null);
  const onDragEnd = () => { setDraggedId(null); setDropTarget(null); };

  const assignTaskTo = async (scope, period_date) => {
    if (!draggedId) return;
    const t = tasks.find(x => x.id === draggedId);
    setDraggedId(null);
    setDropTarget(null);
    if (!t) return;
    const pd = t.period_date ? t.period_date.split('T')[0] : null;
    if (t.scope === scope && pd === period_date) return; // already there
    try {
      await api.updateTask(t.id, { ...t, scope, period_date });
      refreshTasks();
    } catch (err) {
      toast(err.message || 'Konnte nicht zugewiesen werden', 'error');
    }
  };

  const unassignTask = async () => {
    if (!draggedId) return;
    const t = tasks.find(x => x.id === draggedId);
    setDraggedId(null);
    setDropTarget(null);
    if (!t || !t.scope || t.scope === 'task') return;
    try {
      await api.updateTask(t.id, { ...t, scope: 'task', period_date: null });
      refreshTasks();
    } catch (err) {
      toast(err.message || 'Konnte nicht entfernt werden', 'error');
    }
  };

  const cellStyle = {
    border: '1px solid var(--border)',
    padding: '8px 10px',
    verticalAlign: 'top',
    minWidth: 180,
    minHeight: 50,
  };
  const dropHighlight = (key) => dropTarget === key && draggedId
    ? { background: 'var(--accent-dim)', border: '2px solid var(--accent)' } : {};

  const TaskCard = ({ t, compact }) => {
    const goal = t.scope === 'quarter' && t.goal_id ? goalsById[t.goal_id] : null;
    return (
      <div
        draggable
        onDragStart={onDragStart(t.id)}
        onDragEnd={onDragEnd}
        onClick={() => openExisting(t)}
        style={{
          background: 'var(--bg-3)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: compact ? '5px 8px' : '6px 8px',
          marginBottom: 4, cursor: 'grab', color: 'var(--text)', fontSize: 12,
          opacity: draggedId === t.id ? 0.4 : 1,
          userSelect: 'none',
        }}
        title={t.next_step || t.title}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
          {!compact && <StatusBadge status={t.status} />}
        </div>
        {goal && (
          <div style={{ marginTop: 3, fontSize: 10, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: goal.color, display: 'inline-block' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>→ {goal.title}</span>
            {t.scope === 'quarter' && typeof t.progress === 'number' && (
              <span style={{ marginLeft: 'auto' }}>{t.progress}%</span>
            )}
          </div>
        )}
        {!goal && t.scope === 'quarter' && typeof t.progress === 'number' && t.progress > 0 && (
          <div style={{ marginTop: 3, fontSize: 10, color: 'var(--text-3)', textAlign: 'right' }}>{t.progress}%</div>
        )}
      </div>
    );
  };

  const AddBtn = ({ initial }) => (
    <button onClick={() => openNew(initial)} className="btn btn-ghost btn-sm" style={{
      width: '100%', justifyContent: 'center', marginTop: 4, opacity: 0.6,
    }}>
      <Plus size={11} /> Hinzufügen
    </button>
  );

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Side panel */}
      {panelOpen && (
        <div
          style={{
            width: 280, borderRight: '1px solid var(--border)',
            display: 'flex', flexDirection: 'column', background: 'var(--bg-1)',
            ...dropHighlight('panel'),
          }}
          onDragOver={onDragOver('panel')}
          onDragLeave={onDragLeave}
          onDrop={unassignTask}
        >
          <div style={{ padding: 12, borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>Tasks-Pool</span>
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{panelTasks.length}</span>
            </div>
            <div style={{ position: 'relative' }}>
              <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
              <input value={panelSearch} onChange={e => setPanelSearch(e.target.value)}
                placeholder="Filtern..." style={{ paddingLeft: 26, fontSize: 12 }} />
            </div>
            <p style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 6 }}>
              Ziehe Tasks von hier in das Quartal-Raster. Tasks aus dem Raster zurück zu schieben löst die Zuordnung.
            </p>
          </div>
          <div style={{ overflow: 'auto', padding: 12 }}>
            {panelTasks.map(t => <TaskCard key={t.id} t={t} />)}
            {panelTasks.length === 0 && (
              <div style={{ color: 'var(--text-3)', fontSize: 12, textAlign: 'center', padding: 20 }}>
                Keine Tasks ohne Zuordnung
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', gap: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setPanelOpen(o => !o)} title={panelOpen ? 'Panel ausblenden' : 'Panel einblenden'}>
            {panelOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
          </button>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 28, flex: 1 }}>Planung</h1>
          <button className="btn btn-ghost btn-sm" onClick={prevQuarter} title="Vorheriges Quartal"><ChevronLeft size={14} /></button>
          <select value={year} onChange={e => setYear(+e.target.value)} style={{ width: 'auto' }}>
            {Array.from({ length: 7 }, (_, i) => getYear(today) - 2 + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select value={quarter} onChange={e => setQuarter(+e.target.value)} style={{ width: 'auto' }}>
            <option value={1}>Q1</option>
            <option value={2}>Q2</option>
            <option value={3}>Q3</option>
            <option value={4}>Q4</option>
          </select>
          <button className="btn btn-ghost btn-sm" onClick={nextQuarter} title="Nächstes Quartal"><ChevronRight size={14} /></button>
        </div>

        <div style={{ overflow: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-1)' }}>
                <th style={{ ...cellStyle, fontWeight: 500, color: 'var(--text-2)', width: 130, minWidth: 130 }}>KW</th>
                <th style={{ ...cellStyle, fontWeight: 500, color: 'var(--text-2)' }}>Wochen-Ziele</th>
                <th style={{ ...cellStyle, fontWeight: 500, color: 'var(--text-2)', minWidth: 220 }}>Monats-Ziele</th>
                <th style={{ ...cellStyle, fontWeight: 500, color: 'var(--text-2)', minWidth: 220 }}>Quartals-Ziele</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w) => {
                const weekKey = `w-${w.monday}`;
                const monthKey = `m-${w.monthIndex}`;
                const quarterKey = 'q';
                const weekTasks = tasksByCell.weeks[w.monday] || [];
                const monthTasks = tasksByCell.months[w.monthIndex] || [];
                return (
                  <tr key={w.monday}>
                    <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 500, background: 'var(--bg-1)' }}>
                      <div style={{ fontSize: 13 }}>{w.weekNum}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2, fontWeight: 400, whiteSpace: 'nowrap' }}>
                        {format(w.mondayDate, 'dd. MMM', { locale: de })} – {format(w.sundayDate, 'dd. MMM', { locale: de })}
                      </div>
                      {w.isFirstOfMonth && (
                        <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2, fontWeight: 400 }}>{w.monthName}</div>
                      )}
                    </td>
                    <td
                      style={{ ...cellStyle, ...dropHighlight(weekKey) }}
                      onDragOver={onDragOver(weekKey)}
                      onDragLeave={onDragLeave}
                      onDrop={() => assignTaskTo('week', w.monday)}
                    >
                      {weekTasks.map(t => <TaskCard key={t.id} t={t} compact />)}
                      <AddBtn initial={{ scope: 'week', period_date: w.monday }} />
                    </td>
                    {w.isFirstOfMonth && (
                      <td
                        rowSpan={w.weeksInMonth}
                        style={{ ...cellStyle, background: 'var(--bg-1)', ...dropHighlight(monthKey) }}
                        onDragOver={onDragOver(monthKey)}
                        onDragLeave={onDragLeave}
                        onDrop={() => assignTaskTo('month', monthPeriodDate(year, w.monthIndex))}
                      >
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6 }}>{w.monthName}</div>
                        {monthTasks.map(t => <TaskCard key={t.id} t={t} />)}
                        <AddBtn initial={{ scope: 'month', period_date: monthPeriodDate(year, w.monthIndex) }} />
                      </td>
                    )}
                    {w.isFirstWeekOfQuarter && (
                      <td
                        rowSpan={totalWeeks}
                        style={{ ...cellStyle, background: 'var(--bg-2)', ...dropHighlight(quarterKey) }}
                        onDragOver={onDragOver(quarterKey)}
                        onDragLeave={onDragLeave}
                        onDrop={() => assignTaskTo('quarter', qPeriod)}
                      >
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6 }}>Q{quarter} {year}</div>
                        {tasksByCell.quarter.map(t => <TaskCard key={t.id} t={t} />)}
                        <AddBtn initial={{ scope: 'quarter', period_date: qPeriod }} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <TaskModal
          task={editTask}
          initial={modalInitial}
          onClose={() => setShowModal(false)}
          onSaved={refreshTasks}
          onNavigate={(t) => { setEditTask(t); setModalInitial(null); }}
        />
      )}
    </div>
  );
}
